<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEditMode ? '編輯訂位' : '新增訂位' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button
          v-if="isEditMode || currentStep === 3"
          type="button"
          class="btn btn-primary"
          :disabled="submitting || memberLookupLoading || !form.storeId"
          @click="handleSubmit"
        >
          <span v-if="submitting">{{ isEditMode ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEditMode ? '更新訂位' : '建立訂位' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
      <div class="create-reservation-container">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>

        <div v-else class="form-container">
          <form @submit.prevent="handleSubmit">
            <div v-if="!isEditMode" class="reservation-steps">
              <div
                v-for="step in steps"
                :key="step.number"
                class="reservation-step"
                :class="{ active: currentStep === step.number, completed: currentStep > step.number }"
              >
                <span>{{ step.number }}</span>
                <small>{{ step.label }}</small>
              </div>
            </div>

            <div v-show="isEditMode || currentStep === 1 || currentStep === 2" class="form-section">
              <h3 class="section-title">{{ scheduleTitle }}</h3>

              <div class="booking-filters" :class="{ 'is-invalid': errors.reservationDate || errors.guestCount }">
                <button
                  type="button"
                  class="booking-filter booking-date-btn"
                  :aria-expanded="datePickerOpen"
                  @click="toggleDatePicker"
                >
                  <span>用餐日期</span>
                  <strong>{{ dateLabel }}</strong>
                </button>
                <label class="booking-filter">
                  <span>用餐人數</span>
                  <select
                    id="guestCount"
                    v-model.number="form.guestCount"
                    aria-label="用餐人數"
                  >
                    <option v-for="count in guestOptions" :key="count" :value="count">{{ count }} 位</option>
                  </select>
                </label>
                <div v-if="datePickerOpen" class="date-picker-panel">
                  <div class="date-picker-nav">
                    <button type="button" class="date-nav-btn" :disabled="!canPrevMonth" aria-label="上個月" @click="shiftCalendarMonth(-1)">‹</button>
                    <strong>{{ calendarMonthLabel }}</strong>
                    <button type="button" class="date-nav-btn" :disabled="!canNextMonth" aria-label="下個月" @click="shiftCalendarMonth(1)">›</button>
                  </div>
                  <div class="date-weekday-row" aria-hidden="true">
                    <span v-for="label in weekdayShort" :key="label">{{ label }}</span>
                  </div>
                  <div class="date-grid" role="grid" aria-label="可選用餐日期">
                    <button
                      v-for="cell in calendarCells"
                      :key="cell.key"
                      type="button"
                      class="date-cell"
                      :class="{
                        'is-empty': cell.empty,
                        'is-selected': cell.date === form.reservationDate,
                        'is-closed': cell.closed,
                        'is-outside': cell.outside
                      }"
                      :disabled="cell.empty || cell.locked || cell.outside"
                      @click="pickDate(cell.date)"
                    >
                      <template v-if="!cell.empty">
                        <span>{{ cell.day }}</span>
                        <small v-if="cell.closed">休</small>
                      </template>
                    </button>
                  </div>
                  <p class="date-picker-hint">
                    {{ currentStep === 1 && !isEditMode
                      ? '先選日期與人數。各門市公休會標在卡片上，點時間後可再改座位或包廂。'
                      : '公休與整日店休不可選。變更人數會重新計算可訂時段。' }}
                  </p>
                </div>
                <div v-if="periodTabs.length" class="slot-tabs" role="tablist" aria-label="時段">
                  <button
                    type="button"
                    role="tab"
                    :class="{ 'is-selected': slotView === 'all' }"
                    :aria-selected="slotView === 'all'"
                    @click="slotView = 'all'"
                  >
                    整天
                  </button>
                  <button
                    v-for="label in periodTabs"
                    :key="label"
                    type="button"
                    role="tab"
                    :class="{ 'is-selected': slotView === label }"
                    :aria-selected="slotView === label"
                    @click="slotView = label"
                  >
                    {{ label }}
                  </button>
                </div>
              </div>

              <div v-show="isEditMode || currentStep === 1" class="store-board">
                <p v-if="boardsLoading" class="form-hint">正在查詢可訂時段</p>
                <p v-else-if="!stores.length" class="form-hint">目前沒有開放預約的門市。</p>
                <p v-if="errors.storeId" class="invalid-feedback d-block">{{ errors.storeId }}</p>
                <article
                  v-for="store in stores"
                  :key="store.id"
                  class="store-board-card"
                  :class="{ 'is-selected': form.storeId === store.id }"
                >
                  <button type="button" class="store-board-summary" @click="openStoreSchedule(store)">
                    <img
                      v-if="getStoreImage(store)"
                      :src="getStoreImage(store)"
                      :alt="store.storeName"
                    />
                    <div v-else class="store-board-photo" aria-hidden="true">
                      <i class="ri-store-2-line"></i>
                    </div>
                    <div class="store-board-copy">
                      <h4>{{ store.storeName }}</h4>
                      <p>{{ formatStoreAddress(store) }}</p>
                      <p v-if="storePhone(store)" class="store-board-phone">門市電話 {{ storePhone(store) }}</p>
                      <div v-if="store.bookingNotice" class="store-board-notice">
                        <strong>預約須知</strong>
                        <span>{{ store.bookingNotice }}</span>
                      </div>
                    </div>
                  </button>
                  <div v-if="previewTimesFor(store.id).length" class="time-row">
                    <button
                      v-for="slot in previewTimesFor(store.id)"
                      :key="slot.time"
                      type="button"
                      class="time-chip"
                      :disabled="boardsLoading"
                      @click="selectPreviewSlot(store, slot)"
                    >
                      {{ formatTimeToMinute(slot.time) }}
                    </button>
                    <button
                      v-if="hasMoreTimes(store.id)"
                      type="button"
                      class="time-chip more-times"
                      aria-label="查看更多時段"
                      @click="openStoreSchedule(store)"
                    >
                      ...
                    </button>
                  </div>
                  <p v-else-if="!boardsLoading" class="store-board-empty">{{ cardEmptyLabel(store) }}</p>
                </article>
              </div>

              <p v-if="selectedStore && (isEditMode || currentStep === 2)" class="form-hint">
                {{ selectedStore.advanceBookingMode === 'days'
                  ? `此門市採提前天數模式，最早可預約日為 ${minDate}`
                  : `此門市採提前時數模式（最少提前 ${selectedStore.minAdvanceHours || 0} 小時）` }}
              </p>
              <div v-if="selectedStore?.bookingNotice && (isEditMode || currentStep === 2)" class="booking-notice">
                <h4>訂位須知</h4>
                <p>{{ selectedStore.bookingNotice }}</p>
              </div>
              <div v-if="errors.reservationDate" class="invalid-feedback d-block">{{ errors.reservationDate }}</div>
              <div v-if="errors.guestCount" class="invalid-feedback d-block">{{ errors.guestCount }}</div>

              <template v-if="isEditMode || currentStep === 2">
              <p v-if="slotsLoading" class="form-hint">載入可預約時段中...</p>
              <p
                v-else-if="form.storeId && tablesLoaded && tables.length === 0"
                class="form-hint form-hint-warning"
              >
                此門市尚未設定座位，無法產生可預約時段。請先至「座位管理」為此門市新增座位。
              </p>
              <p v-else-if="allDayClosure" class="form-hint form-hint-warning">
                此日期為特殊店休：{{ allDayClosure.name }}，無法預約。
              </p>
              <p v-else-if="isWeeklyRestDate" class="form-hint form-hint-warning">
                此日期為每週固定公休日，無法預約。
              </p>
              <p
                v-else-if="form.storeId && form.reservationDate && !slotsLoading && !availableSlots.length"
                class="form-hint form-hint-warning"
              >
                {{ emptySlotsMessage }}
              </p>
              <p
                v-else-if="form.storeId && form.reservationDate && !slotsLoading && groupedSlots.length === 0"
                class="form-hint"
              >
                此時段沒有可顯示的時間。
              </p>

              <section v-for="group in groupedSlots" :key="group.label" class="period-group">
                <h4>{{ group.label }}</h4>
                <div class="time-grid">
                  <button
                    v-for="slot in group.slots"
                    :key="slot.time"
                    type="button"
                    class="time-chip"
                    :class="{ 'is-selected': form.reservationTime === slot.time }"
                    :disabled="!slot.available || slotsLoading"
                    @click="selectTimeSlot(slot)"
                  >
                    {{ formatTimeToMinute(slot.time) }}
                  </button>
                </div>
              </section>
              <div v-if="errors.reservationTime" class="invalid-feedback d-block">
                {{ errors.reservationTime }}
              </div>

              <div v-if="form.reservationTime" class="seat-board" :class="{ 'is-invalid': errors.tableId }">
                <div class="seat-board-head">
                  <h4>座位與包廂</h4>
                  <p>
                    已依 {{ form.guestCount }} 位先分配空位，可改選其他座位或包廂。
                    {{ allowUpwardTableAssignment
                      ? '符合人數的桌已滿時，一般座位可向上相容較大桌（包廂除外）。'
                      : '目前門市未開啟向上相容，只顯示人數符合的空位。' }}
                  </p>
                </div>

                <div v-if="regularSeats.length" class="seat-group">
                  <h5>座位</h5>
                  <div class="seat-grid">
                    <button
                      v-for="table in regularSeats"
                      :key="table.id"
                      type="button"
                      class="seat-card"
                      :class="{
                        'is-selected': form.tableId === table.id,
                        'is-upward': isUpwardTable(table)
                      }"
                      @click="selectSeat(table)"
                    >
                      <strong>{{ table.tableNumber }}</strong>
                      <span>{{ formatCapacity(table) }}</span>
                      <small v-if="isUpwardTable(table)">向上相容</small>
                    </button>
                  </div>
                </div>

                <div v-if="privateRooms.length" class="seat-group">
                  <h5>包廂</h5>
                  <div class="seat-grid">
                    <button
                      v-for="table in privateRooms"
                      :key="table.id"
                      type="button"
                      class="seat-card seat-card-room"
                      :class="{ 'is-selected': form.tableId === table.id }"
                      @click="selectSeat(table)"
                    >
                      <strong>{{ table.tableNumber }}</strong>
                      <span>{{ roomCardMeta(table) }}</span>
                      <small v-if="table.roomMinSpend">低消 NT${{ formatCurrency(table.roomMinSpend) }}</small>
                    </button>
                  </div>
                </div>

                <p v-if="!selectableTables.length" class="form-hint form-hint-warning">
                  此時段沒有可分配的座位或包廂。
                </p>
                <div
                  v-if="selectedTableMatch?.isUpwardAssignment"
                  class="form-hint form-hint-warning"
                >
                  目前為向上相容座位：用餐人數低於「{{ selectedTable.tableNumber }}」建議下限
                  {{ selectedTable.minCapacity || 1 }} 人。
                </div>
                <div v-if="errors.tableId" class="invalid-feedback d-block">
                  {{ errors.tableId }}
                </div>
                <div v-if="selectedTable?.tableType === '包廂'" class="room-summary">
                  <div class="room-summary-item">
                    <span class="room-summary-label">包廂低消：</span>
                    <span class="room-summary-value">
                      {{ selectedTable.roomMinSpend != null
                        ? `NT$ ${formatCurrency(selectedTable.roomMinSpend)}`
                        : '未設定' }}
                    </span>
                  </div>
                  <div class="room-summary-item">
                    <span class="room-summary-label">包廂說明：</span>
                    <span class="room-summary-value">{{ getRoomDescription(selectedTable) }}</span>
                  </div>
                </div>
              </div>
              <p v-else-if="groupedSlots.length" class="form-hint">請先選擇時段，再調整座位或包廂。</p>
              </template>
            </div>

            <div v-show="isEditMode || currentStep === 3" class="form-section">
              <h3 class="section-title">{{ isEditMode ? '聯絡人資料' : 'Step 3：填寫聯絡人與用餐目的' }}</h3>

              <div v-if="selectedStore && form.reservationTime" class="booking-summary">
                <strong>{{ selectedStore.storeName }}</strong>
                <span>{{ dateLabel }} {{ formatTimeToMinute(form.reservationTime) }}</span>
                <span>{{ form.guestCount }} 位</span>
                <span v-if="selectedTable">
                  {{ selectedTable.tableType === '包廂' ? '包廂' : '座位' }} {{ selectedTable.tableNumber }}
                </span>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="contactPhone" class="required">聯絡電話</label>
                  <div class="phone-lookup-field">
                    <input
                      id="contactPhone"
                      v-model="form.contactPhone"
                      type="tel"
                      class="form-control"
                      :class="{ 'is-invalid': errors.contactPhone }"
                      placeholder="請輸入聯絡電話"
                      maxlength="20"
                      required
                      @input="handlePhoneInput"
                      @blur="lookupMemberByPhone"
                    />
                    <span v-if="memberLookupLoading" class="form-hint">會員查詢中...</span>
                  </div>
                  <div v-if="errors.contactPhone" class="invalid-feedback">
                    {{ errors.contactPhone }}
                  </div>
                </div>

                <div class="form-group">
                  <label for="contactName" class="required">聯絡人姓名</label>
                  <input
                    id="contactName"
                    v-model="form.contactName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.contactName }"
                    placeholder="請輸入聯絡人姓名"
                    maxlength="100"
                    required
                  />
                  <div v-if="errors.contactName" class="invalid-feedback">
                    {{ errors.contactName }}
                  </div>
                </div>
              </div>

              <div v-if="matchedMember" class="matched-member-card">
                <i class="ri-user-heart-line"></i>
                <div>
                  <strong>{{ matchedMember.name }}</strong>
                  <span>
                    會員編號：{{ matchedMember.memberCode || '-' }}
                    <template v-if="matchedMember.tierName">・{{ matchedMember.tierName }}</template>
                  </span>
                  <span v-if="matchedMember.email">{{ matchedMember.email }}</span>
                </div>
              </div>
              <p v-else-if="memberLookupDone && form.contactPhone" class="form-hint">
                此電話未找到會員，將以訪客資料建立訂位。
              </p>

              <div class="form-group">
                <label>用餐目的</label>
                <div class="occasion-row">
                  <button
                    v-for="item in occasionOptions"
                    :key="item"
                    type="button"
                    class="occasion-chip"
                    :class="{ 'is-selected': form.occasion === item }"
                    @click="toggleOccasion(item)"
                  >
                    {{ item }}
                  </button>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group full-width">
                  <label for="specialRequests">特殊需求</label>
                  <textarea
                    id="specialRequests"
                    v-model="form.specialRequests"
                    class="form-control"
                    rows="3"
                    placeholder="例如：需要兒童座椅、慶生等"
                  ></textarea>
                </div>
              </div>
            </div>

            <div v-if="!isEditMode" class="wizard-actions">
              <button
                v-if="currentStep > 1"
                type="button"
                class="btn btn-secondary"
                :disabled="submitting || memberLookupLoading"
                @click="previousStep"
              >
                上一步
              </button>
              <button
                v-if="currentStep < 3"
                type="button"
                class="btn btn-primary"
                :disabled="submitting || (currentStep === 1 && !stores.length)"
                @click="nextStep"
              >
                下一步
              </button>
              <button
                v-if="currentStep === 3"
                type="button"
                class="btn btn-primary"
                :disabled="submitting || memberLookupLoading"
                @click="handleSubmit"
              >
                <span v-if="submitting">建立中...</span>
                <span v-else>建立訂位</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { reservationService } from '@/api/modules/reservations.js';
import { storeService } from '@/api/modules/stores.js';
import { memberService } from '@/api/modules/members.js';
import { showSuccess, showError, showWarning } from '@/utils/swal.js';

export default {
  name: 'CreateReservation',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const stores = ref([]);
    const tables = ref([]);
    const tablesLoaded = ref(false);
    const availableSlots = ref([]);
    const dayClosures = ref([]);
    const storeHours = ref([]);
    const allowUpwardTableAssignment = ref(false);
    const slotsLoading = ref(false);
    const loading = ref(false);
    const submitting = ref(false);
    const errors = ref({});
    const isEditMode = ref(false);
    const currentStep = ref(1);
    const matchedMember = ref(null);
    const memberLookupLoading = ref(false);
    const memberLookupDone = ref(false);
    const datePickerOpen = ref(false);
    const calendarMonth = ref('');
    const slotView = ref('all');
    const monthClosures = ref([]);
    const storeBoards = ref({});
    const hoursByStore = ref({});
    const closuresByStore = ref({});
    const boardsLoading = ref(false);
    const previewSlotLimit = ref(6);
    const weekdayShort = ['日', '一', '二', '三', '四', '五', '六'];
    const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
    let slotsRequestSeq = 0;

    const steps = [
      { number: 1, label: '選擇門市' },
      { number: 2, label: '日期時段座位' },
      { number: 3, label: '聯絡人與用餐目的' }
    ];

    const form = ref({
      storeId: '',
      reservationDate: '',
      reservationTime: '',
      guestCount: 1,
      contactName: '',
      contactPhone: '',
      memberId: null,
      tableId: '',
      occasion: '',
      specialRequests: ''
    });

    const baseOccasionOptions = ['慶生', '約會', '商務', '家庭聚餐', '朋友聚會', '其他'];
    const occasionOptions = computed(() => {
      const current = String(form.value.occasion || '');
      if (current && !baseOccasionOptions.includes(current)) {
        return [...baseOccasionOptions, current];
      }
      return baseOccasionOptions;
    });
    const selectedStore = computed(() => stores.value.find(store => store.id === form.value.storeId) || null);
    const scheduleTitle = computed(() => {
      if (isEditMode.value) return '門市、日期與座位';
      if (currentStep.value === 1) return 'Step 1：選擇門市';
      return 'Step 2：選擇日期、時段與座位';
    });
    const guestOptions = computed(() => {
      const max = Math.max(30, Number(form.value.guestCount) || 1);
      return Array.from({ length: max }, (_, index) => index + 1);
    });
    const dateLabel = computed(() => {
      if (!form.value.reservationDate) return '請選擇日期';
      const current = new Date(`${form.value.reservationDate}T00:00:00`);
      if (Number.isNaN(current.getTime())) return form.value.reservationDate;
      return `${current.getMonth() + 1}月${current.getDate()}日（${weekdays[current.getDay()]}）`;
    });
    const calendarMonthLabel = computed(() => {
      const [year, month] = String(calendarMonth.value || '').split('-');
      if (!year || !month) return '';
      return `${Number(year)}年${Number(month)}月`;
    });
    const maxDate = computed(() => {
      const today = new Date();
      return formatDateToLocalString(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 365));
    });

    const getRoomCapacityRange = (table) => {
      if (!table) return null;
      const minCapacity = Number(table.minCapacity || 1);
      const maxCapacity = Number(table.maxCapacity || table.capacity || minCapacity);
      return { minCapacity, maxCapacity };
    };

    const getSelectedTableMatch = () => {
      const slot = availableSlots.value.find(
        (item) => item.time === form.value.reservationTime && item.available
      );
      return (slot?.availableTables || []).find((item) => item.tableId === form.value.tableId) || null;
    };

    const warnIfGuestCountOutOfRange = async ({ announce = true } = {}) => {
      const table = tables.value.find((item) => item.id === form.value.tableId);
      if (!table) return true;

      const guestCount = Number(form.value.guestCount);
      if (!Number.isFinite(guestCount) || guestCount < 1) {
        if (announce) {
          await showWarning('請先填寫用餐人數', '選擇座位前請先確認用餐人數。');
        }
        return false;
      }

      const range = getRoomCapacityRange(table);
      if (!range) return true;
      const match = getSelectedTableMatch();

      if (match?.isUpwardAssignment || (guestCount < range.minCapacity && guestCount <= range.maxCapacity && table.tableType !== '包廂')) {
        if (announce) {
          await showWarning(
            '向上相容座位',
            `符合人數的座位已不足，「${table.tableNumber}」為向上相容（建議 ${range.minCapacity}-${range.maxCapacity} 人，目前 ${guestCount} 人）。訂位會標示此狀態。`
          );
        }
        return true;
      }

      if (guestCount < range.minCapacity || guestCount > range.maxCapacity) {
        if (announce) {
          await showWarning(
            table.tableType === '包廂' ? '包廂人數不符合範圍' : '人數超出座位範圍',
            `「${table.tableNumber}」需介於 ${range.minCapacity}-${range.maxCapacity} 人（目前 ${guestCount} 人）。${table.tableType === '包廂' ? '包廂不可向上相容。' : ''}`
          );
        }
        form.value.tableId = '';
        return false;
      }

      return true;
    };

    const validateSelectedTableCapacity = async ({ announce = true } = {}) => (
      warnIfGuestCountOutOfRange({ announce })
    );

    const handleTableChange = async () => {
      await validateSelectedTableCapacity({ announce: true });
      if (form.value.tableId) {
        clearStepErrors(['tableId']);
      }
    };

    const selectSeat = async (table) => {
      if (!table?.id || form.value.tableId === table.id) return;
      form.value.tableId = table.id;
      await handleTableChange();
    };

    const toggleOccasion = (item) => {
      form.value.occasion = form.value.occasion === item ? '' : item;
    };

    const formatCapacity = (table) => {
      if (!table) return '';
      const minCapacity = Number(table.minCapacity || 1);
      const maxCapacity = Number(table.maxCapacity || table.capacity || minCapacity);
      if (minCapacity === maxCapacity) return `${maxCapacity} 人`;
      return `${minCapacity}-${maxCapacity} 人`;
    };

    const isUpwardTable = (table) => Boolean(getSelectedTableMatchForTable(table?.id)?.isUpwardAssignment);

    const roomCardMeta = (table) => {
      const category = table?.roomCategory ? `${table.roomCategory} · ` : '';
      return `${category}${formatCapacity(table)}`;
    };

    const periodLabelOf = (slot) => String(slot?.periodName || '').trim() || '時段';

    const formatTableOptionLabel = (table) => {
      if (!table) return '';
      const minCapacity = table.minCapacity || 1;
      const maxCapacity = table.maxCapacity || table.capacity;
      const slotMatch = getSelectedTableMatchForTable(table.id);
      const upwardTag = slotMatch?.isUpwardAssignment ? '・向上相容' : '';
      if (table.tableType === '包廂') {
        const category = table.roomCategory ? `${table.roomCategory} / ` : '';
        const minSpend = table.roomMinSpend
          ? ` / 低消NT$${Number(table.roomMinSpend).toLocaleString('zh-TW')}`
          : '';
        return `${table.tableNumber}（${category}${minCapacity}-${maxCapacity}人包廂${minSpend}）`;
      }
      return `${table.tableNumber}（${minCapacity}-${maxCapacity}人${upwardTag}）`;
    };

    const getSelectedTableMatchForTable = (tableId) => {
      const slot = availableSlots.value.find(
        (item) => item.time === form.value.reservationTime && item.available
      );
      return (slot?.availableTables || []).find((item) => item.tableId === tableId) || null;
    };

    const autoAssignTableForSelectedSlot = ({ force = false } = {}) => {
      const slot = availableSlots.value.find(
        (item) => item.time === form.value.reservationTime && item.available
      );
      const availableOptions = slot?.availableTables || [];
      const availableIds = availableOptions.map((item) => item.tableId);
      if (!availableIds.length) {
        if (!isEditMode.value || force) {
          form.value.tableId = '';
        }
        return;
      }

      if (!force && form.value.tableId && availableIds.includes(form.value.tableId)) {
        return;
      }

      // 優先符合人數範圍，其次向上相容
      const preferred = availableOptions.find((item) => item.matchType === 'exact' || item.fitsGuestCount === true)
        || availableOptions.find((item) => item.matchType === 'upward' || item.isUpwardAssignment)
        || availableOptions[0];
      form.value.tableId = preferred.tableId;
      clearStepErrors(['tableId']);
    };
    const formatDateToLocalString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const earliestDateForStore = (store) => {
      const today = new Date();
      if (store?.advanceBookingMode === 'days') {
        const days = Number(store.advanceBookingDays || 0);
        return formatDateToLocalString(new Date(today.getFullYear(), today.getMonth(), today.getDate() + days));
      }
      return formatDateToLocalString(today);
    };
    const minDate = computed(() => {
      const today = formatDateToLocalString(new Date());
      if (!stores.value.length) return today;
      if ((isEditMode.value || currentStep.value >= 2) && selectedStore.value) {
        return earliestDateForStore(selectedStore.value);
      }
      return stores.value.reduce((earliest, store) => {
        const value = earliestDateForStore(store);
        return value < earliest ? value : earliest;
      }, earliestDateForStore(stores.value[0]));
    });
    const selectableSlots = computed(() => availableSlots.value.filter(slot => slot?.available));
    const emptySlotsMessage = computed(() => {
      if (allDayClosure.value) return `此日期為特殊店休：${allDayClosure.value.name}，無法預約。`;
      if (isWeeklyRestDate.value) return '此日期為每週固定公休日，無法預約。';
      if (allowUpwardTableAssignment.value) {
        return '此時段已滿位（含向上相容大桌仍無空位），或未開放營業／店休。';
      }
      return '此時段已滿位（符合人數的座位已滿；門市未開啟向上相容改派大桌），或未開放營業／店休。';
    });
    const selectableTables = computed(() => {
      const slot = availableSlots.value.find(
        (item) => item.time === form.value.reservationTime && item.available
      );
      const availableOptions = slot?.availableTables || [];
      const orderMap = new Map(availableOptions.map((item, index) => [item.tableId, index]));
      const availableIds = new Set(availableOptions.map((item) => item.tableId));
      if (form.value.tableId) {
        availableIds.add(form.value.tableId);
      }
      return tables.value
        .filter((table) => availableIds.has(table.id))
        .sort((left, right) => {
          const leftOrder = orderMap.has(left.id) ? orderMap.get(left.id) : Number.MAX_SAFE_INTEGER;
          const rightOrder = orderMap.has(right.id) ? orderMap.get(right.id) : Number.MAX_SAFE_INTEGER;
          if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          return Number(left.capacity || 0) - Number(right.capacity || 0);
        });
    });
    const selectedTable = computed(() => tables.value.find((table) => table.id === form.value.tableId) || null);
    const selectedTableMatch = computed(() => getSelectedTableMatch());
    const allDayClosure = computed(() => dayClosures.value.find((item) => item.isAllDay) || null);
    const isWeeklyRestDate = computed(() => {
      if (!form.value.reservationDate || !storeHours.value.length) return false;
      return isWeeklyClosedDate(form.value.reservationDate);
    });

    const isWeeklyClosedDate = (dateStr) => {
      if (!dateStr || !storeHours.value.length) return false;
      const date = new Date(`${dateStr}T00:00:00`);
      if (Number.isNaN(date.getTime())) return false;
      const matched = storeHours.value.find((item) => Number(item.dayOfWeek) === date.getDay());
      return matched ? matched.isOpen === false : false;
    };

    const closureNameForDate = (dateStr) => {
      const hit = monthClosures.value.find((item) => (
        item?.isAllDay
        && item.startDate
        && item.endDate
        && item.startDate <= dateStr
        && item.endDate >= dateStr
      ));
      return hit?.name || '';
    };

    const isStoreWeeklyClosed = (storeId, dateStr) => {
      const hours = hoursByStore.value[storeId] || [];
      if (!dateStr || !hours.length) return false;
      const date = new Date(`${dateStr}T00:00:00`);
      if (Number.isNaN(date.getTime())) return false;
      const matched = hours.find((item) => Number(item.dayOfWeek) === date.getDay());
      return matched ? matched.isOpen === false : false;
    };

    const storeHasAllDayClosure = (storeId, dateStr) => (
      (closuresByStore.value[storeId] || []).some((item) => (
        item?.isAllDay
        && item.startDate
        && item.endDate
        && item.startDate <= dateStr
        && item.endDate >= dateStr
      ))
    );

    const isStoreClosedOnDate = (storeId, dateStr) => (
      isStoreWeeklyClosed(storeId, dateStr) || storeHasAllDayClosure(storeId, dateStr)
    );

    const isClosedOnCalendar = (dateStr) => {
      if (!stores.value.length) {
        return isWeeklyClosedDate(dateStr) || Boolean(closureNameForDate(dateStr));
      }
      const useSelectedStore = Boolean(form.value.storeId) && (isEditMode.value || currentStep.value >= 2);
      if (useSelectedStore) return isStoreClosedOnDate(form.value.storeId, dateStr);
      return stores.value.every((store) => isStoreClosedOnDate(store.id, dateStr));
    };

    const browseSlots = computed(() => {
      if (!isEditMode.value && currentStep.value === 1) {
        return Object.values(storeBoards.value).flatMap((board) => board?.slots || []);
      }
      return availableSlots.value;
    });
    const periodTabs = computed(() => {
      const labels = [];
      browseSlots.value.forEach((slot) => {
        const label = periodLabelOf(slot);
        if (!labels.includes(label)) labels.push(label);
      });
      return labels;
    });

    const groupedSlots = computed(() => {
      const groups = [];
      const index = new Map();
      availableSlots.value.forEach((slot) => {
        const label = periodLabelOf(slot);
        if (slotView.value !== 'all' && label !== slotView.value) return;
        if (!index.has(label)) {
          const group = { label, slots: [] };
          index.set(label, group);
          groups.push(group);
        }
        index.get(label).slots.push(slot);
      });
      return groups;
    });

    const regularSeats = computed(() => selectableTables.value.filter((table) => table.tableType !== '包廂'));
    const privateRooms = computed(() => selectableTables.value.filter((table) => table.tableType === '包廂'));

    const canPrevMonth = computed(() => calendarMonth.value > minDate.value.slice(0, 7));
    const canNextMonth = computed(() => calendarMonth.value < maxDate.value.slice(0, 7));
    const calendarCells = computed(() => {
      const [yearText, monthText] = String(calendarMonth.value || '').split('-');
      const year = Number(yearText);
      const month = Number(monthText);
      if (!year || !month) return [];
      const first = new Date(year, month - 1, 1);
      const daysInMonth = new Date(year, month, 0).getDate();
      const cells = [];
      for (let index = 0; index < first.getDay(); index += 1) {
        cells.push({ key: `pad-${index}`, empty: true });
      }
      for (let day = 1; day <= daysInMonth; day += 1) {
        const value = `${yearText}-${monthText}-${String(day).padStart(2, '0')}`;
        const isCurrent = value === form.value.reservationDate;
        const outside = (value < minDate.value || value > maxDate.value) && !isCurrent;
        const closed = isClosedOnCalendar(value);
        cells.push({
          key: value,
          empty: false,
          date: value,
          day,
          closed,
          outside,
          locked: closed && !isCurrent
        });
      }
      return cells;
    });

    const ensureNearestReservationDate = (force = false) => {
      if (isEditMode.value) return;
      if (force || !form.value.reservationDate || form.value.reservationDate < minDate.value) {
        form.value.reservationDate = minDate.value;
      }
    };

    const clearStepErrors = (fields) => {
      const nextErrors = { ...errors.value };
      fields.forEach((field) => {
        delete nextErrors[field];
      });
      errors.value = nextErrors;
    };

    const validateStep = (step) => {
      const nextErrors = {};
      if (step === 1) {
        if (!form.value.storeId) nextErrors.storeId = '請選擇門市';
      }
      if (step === 2) {
        if (!form.value.reservationDate) nextErrors.reservationDate = '請選擇訂位日期';
        if (!form.value.reservationTime) nextErrors.reservationTime = '請選擇訂位時間';
        if (!form.value.guestCount || form.value.guestCount < 1) nextErrors.guestCount = '請輸入用餐人數';
        if (!form.value.tableId) nextErrors.tableId = '請選擇座位';
      }
      if (step === 3) {
        if (!form.value.contactPhone?.trim()) nextErrors.contactPhone = '請輸入聯絡電話';
        if (!form.value.contactName?.trim()) nextErrors.contactName = '請輸入聯絡人姓名';
      }
      errors.value = nextErrors;
      return Object.keys(nextErrors).length === 0;
    };

    const nextStep = async () => {
      if (!validateStep(currentStep.value)) {
        await showError('請先完成目前步驟');
        return;
      }
      if (currentStep.value === 2 && !(await validateSelectedTableCapacity({ announce: true }))) {
        return;
      }
      if (currentStep.value < 3) {
        currentStep.value += 1;
        if (currentStep.value === 2) {
          ensureNearestReservationDate();
          await Promise.all([fetchTables(), fetchAvailableSlots()]);
        }
      }
    };

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value -= 1;
      }
    };

    const normalizedMemberPhone = (value = form.value.contactPhone) => {
      const digits = String(value || '').replace(/\D/g, '');
      if (digits.startsWith('886') && digits.length === 12) return `0${digits.slice(3)}`;
      return digits;
    };

    const handlePhoneInput = () => {
      const currentPhone = normalizedMemberPhone();
      const matchedPhone = normalizedMemberPhone(matchedMember.value?.phone);
      if (!matchedMember.value || currentPhone !== matchedPhone) {
        matchedMember.value = null;
        form.value.memberId = null;
      }
      memberLookupDone.value = false;
    };

    const lookupMemberByPhone = async () => {
      const phone = normalizedMemberPhone();
      if (!/^09\d{8}$/.test(phone)) return true;
      if (memberLookupLoading.value) return false;
      if (matchedMember.value && normalizedMemberPhone(matchedMember.value.phone) === phone) {
        memberLookupDone.value = true;
        return true;
      }

      memberLookupLoading.value = true;
      memberLookupDone.value = false;
      try {
        const response = await memberService.getMembersByIdentifiers([phone]);
        if (normalizedMemberPhone() !== phone) return true;
        const members = response?.data?.items || response?.data || [];
        const member = members.find((item) => normalizedMemberPhone(item.phone) === phone) || null;
        matchedMember.value = member;
        form.value.memberId = member?.id || null;
        if (member?.name) {
          form.value.contactName = member.name;
        }
        memberLookupDone.value = true;
        return true;
      } catch (err) {
        if (normalizedMemberPhone() === phone) {
          matchedMember.value = null;
          form.value.memberId = null;
          memberLookupDone.value = true;
          showError(err.response?.data?.message || '會員查詢失敗');
        }
        return false;
      } finally {
        memberLookupLoading.value = false;
      }
    };

    const fetchStores = async () => {
      try {
        const response = await storeService.getStores({
          page: 1,
          pageSize: 100,
          bookingEnabled: true
        });

        if (response.success && response.data?.items) {
          stores.value = response.data.items.filter((store) => store.bookingEnabled);
          ensureNearestReservationDate();
          await Promise.all([fetchAllStoreHours(), fetchAllMonthClosures()]);
        }
      } catch (err) {
        console.error('[CreateReservation] Failed to fetch stores:', err);
      }
    };

    const fetchTables = async () => {
      if (!form.value.storeId) {
        tables.value = [];
        tablesLoaded.value = false;
        return;
      }

      tablesLoaded.value = false;
      try {
        const response = await reservationService.getTables(form.value.storeId, {
          page: 1,
          pageSize: 100,
          isActive: true
        });
        if (response.success && response.data?.items) {
          tables.value = response.data.items;
        } else {
          tables.value = [];
        }
      } catch (err) {
        console.error('Failed to fetch tables:', err);
        tables.value = [];
      } finally {
        tablesLoaded.value = true;
      }
    };

    const fetchAvailableSlots = async () => {
      if (!form.value.storeId || !form.value.reservationDate) {
        availableSlots.value = [];
        dayClosures.value = [];
        allowUpwardTableAssignment.value = false;
        return;
      }

      const seq = ++slotsRequestSeq;
      const requestedTime = form.value.reservationTime;
      const requestedTableId = form.value.tableId;
      slotsLoading.value = true;
      try {
        const response = await reservationService.getAvailableSlots(
          form.value.storeId,
          form.value.reservationDate,
          form.value.guestCount,
          isEditMode.value ? route.params.id : undefined
        );
        if (seq !== slotsRequestSeq) return;
        if (response.success && response.data) {
          availableSlots.value = response.data.availableSlots || [];
          dayClosures.value = response.data.closures || [];
          allowUpwardTableAssignment.value = Boolean(response.data.allowUpwardTableAssignment);
          const selectedSlot = availableSlots.value.find(
            (slot) => slot.time === requestedTime && slot.available
          );
          if (!selectedSlot) {
            form.value.reservationTime = '';
            form.value.tableId = '';
          } else {
            form.value.reservationTime = requestedTime;
            const stillAvailable = (selectedSlot.availableTables || []).some(
              (item) => item.tableId === requestedTableId
            );
            if (requestedTableId && stillAvailable) {
              form.value.tableId = requestedTableId;
            } else {
              autoAssignTableForSelectedSlot({ force: true });
            }
          }
        } else {
          availableSlots.value = [];
          dayClosures.value = [];
          allowUpwardTableAssignment.value = false;
          form.value.reservationTime = '';
          form.value.tableId = '';
        }
      } catch (err) {
        if (seq !== slotsRequestSeq) return;
        console.error('[CreateReservation] Failed to fetch available slots:', err);
        availableSlots.value = [];
        dayClosures.value = [];
        allowUpwardTableAssignment.value = false;
        form.value.reservationTime = '';
        form.value.tableId = '';
      } finally {
        if (seq === slotsRequestSeq) slotsLoading.value = false;
      }
    };

    const fetchMonthClosures = async () => {
      if (!form.value.storeId || !calendarMonth.value) {
        monthClosures.value = [];
        return;
      }
      const [yearText, monthText] = calendarMonth.value.split('-');
      const year = Number(yearText);
      const month = Number(monthText);
      if (!year || !month) return;
      const startDate = `${calendarMonth.value}-01`;
      const endDate = formatDateToLocalString(new Date(year, month, 0));
      try {
        const response = await reservationService.getStoreClosuresForCalendar({
          storeId: form.value.storeId,
          startDate,
          endDate
        });
        const items = response?.data?.items || response?.data || [];
        monthClosures.value = Array.isArray(items) ? items : [];
      } catch (err) {
        console.error('[CreateReservation] Failed to fetch month closures:', err);
        monthClosures.value = [];
      }
    };

    const syncBoard = (storeId = form.value.storeId) => {
      const board = storeBoards.value[storeId];
      if (!board) {
        availableSlots.value = [];
        dayClosures.value = [];
        allowUpwardTableAssignment.value = false;
        return;
      }
      availableSlots.value = board.slots || [];
      dayClosures.value = board.closures || [];
      allowUpwardTableAssignment.value = Boolean(board.allowUpward);
    };

    const fetchStoreBoards = async () => {
      if (!form.value.reservationDate || !stores.value.length) {
        if (!form.value.reservationDate) {
          storeBoards.value = {};
          availableSlots.value = [];
          dayClosures.value = [];
        }
        return;
      }

      const seq = ++slotsRequestSeq;
      const requestedTime = form.value.reservationTime;
      const requestedTableId = form.value.tableId;
      const requestedStoreId = form.value.storeId;
      boardsLoading.value = true;
      slotsLoading.value = true;
      try {
        const entries = await Promise.all(stores.value.map(async (store) => {
          try {
            const response = await reservationService.getAvailableSlots(
              store.id,
              form.value.reservationDate,
              form.value.guestCount,
              isEditMode.value && store.id === requestedStoreId ? route.params.id : undefined
            );
            const data = response?.success ? response.data : null;
            return [store.id, {
              slots: data?.availableSlots || [],
              closures: data?.closures || [],
              allowUpward: Boolean(data?.allowUpwardTableAssignment)
            }];
          } catch (err) {
            console.error('[CreateReservation] Failed to fetch store slots:', store.id, err);
            return [store.id, { slots: [], closures: [], allowUpward: false }];
          }
        }));
        if (seq !== slotsRequestSeq) return;
        storeBoards.value = Object.fromEntries(entries);
        if (!requestedStoreId) return;
        syncBoard(requestedStoreId);
        const selectedSlot = availableSlots.value.find(
          (slot) => slot.time === requestedTime && slot.available
        );
        if (!requestedTime) return;
        if (!selectedSlot) {
          form.value.reservationTime = '';
          form.value.tableId = '';
          return;
        }
        form.value.reservationTime = requestedTime;
        const stillAvailable = (selectedSlot.availableTables || []).some(
          (item) => item.tableId === requestedTableId
        );
        if (requestedTableId && stillAvailable) {
          form.value.tableId = requestedTableId;
        } else {
          autoAssignTableForSelectedSlot({ force: true });
        }
      } finally {
        if (seq === slotsRequestSeq) {
          boardsLoading.value = false;
          slotsLoading.value = false;
        }
      }
    };

    const fetchAllStoreHours = async () => {
      const entries = await Promise.all(stores.value.map(async (store) => {
        try {
          const response = await storeService.getStoreHours(store.id);
          return [store.id, response?.success ? (response.data?.hours || []) : []];
        } catch (err) {
          console.error('[CreateReservation] Failed to fetch store hours:', store.id, err);
          return [store.id, []];
        }
      }));
      hoursByStore.value = Object.fromEntries(entries);
      if (form.value.storeId) {
        storeHours.value = hoursByStore.value[form.value.storeId] || storeHours.value;
      }
    };

    const fetchAllMonthClosures = async () => {
      if (!calendarMonth.value || !stores.value.length) {
        closuresByStore.value = {};
        monthClosures.value = [];
        return;
      }
      const [yearText, monthText] = calendarMonth.value.split('-');
      const year = Number(yearText);
      const month = Number(monthText);
      if (!year || !month) return;
      const startDate = `${calendarMonth.value}-01`;
      const endDate = formatDateToLocalString(new Date(year, month, 0));
      const entries = await Promise.all(stores.value.map(async (store) => {
        try {
          const response = await reservationService.getStoreClosuresForCalendar({
            storeId: store.id,
            startDate,
            endDate
          });
          const items = response?.data?.items || response?.data || [];
          return [store.id, Array.isArray(items) ? items : []];
        } catch (err) {
          console.error('[CreateReservation] Failed to fetch month closures:', store.id, err);
          return [store.id, []];
        }
      }));
      closuresByStore.value = Object.fromEntries(entries);
      if (form.value.storeId) {
        monthClosures.value = closuresByStore.value[form.value.storeId] || [];
      }
    };

    const visibleTimesFor = (storeId) => {
      const slots = (storeBoards.value[storeId]?.slots || []).filter((slot) => slot.available);
      if (slotView.value === 'all') return slots;
      return slots.filter((slot) => periodLabelOf(slot) === slotView.value);
    };

    const previewTimesFor = (storeId) => {
      const times = visibleTimesFor(storeId);
      if (times.length <= previewSlotLimit.value) return times;
      return times.slice(0, previewSlotLimit.value);
    };

    const hasMoreTimes = (storeId) => visibleTimesFor(storeId).length > previewSlotLimit.value;

    const cardEmptyLabel = (store) => {
      const board = storeBoards.value[store.id];
      const available = (board?.slots || []).filter((slot) => slot.available);
      if (available.length && !visibleTimesFor(store.id).length) return '此時段沒有可訂時間';
      if (isStoreWeeklyClosed(store.id, form.value.reservationDate)) return '公休';
      const closure = (board?.closures || []).find((item) => item.isAllDay);
      if (closure) return `店休：${closure.name}`;
      if (!(board?.slots || []).length) return '這天沒有可訂時段';
      return '此時段已額滿';
    };

    const storePhone = (store) => String(store?.phone || '').trim();

    const openStoreSchedule = async (store) => {
      if (!store?.id) return;
      const switching = form.value.storeId !== store.id;
      if (switching) {
        form.value.storeId = store.id;
        form.value.reservationTime = '';
        form.value.tableId = '';
        clearStepErrors(['storeId', 'reservationTime', 'tableId']);
        storeHours.value = hoursByStore.value[store.id] || [];
        monthClosures.value = closuresByStore.value[store.id] || [];
        syncBoard(store.id);
        await Promise.all([
          fetchTables(),
          storeHours.value.length ? Promise.resolve() : fetchStoreHours()
        ]);
      } else {
        syncBoard(store.id);
      }
      if (!isEditMode.value) currentStep.value = 2;
    };

    const selectPreviewSlot = async (store, slot) => {
      if (!slot?.available || !store?.id) return;
      const switching = form.value.storeId !== store.id;
      if (switching) {
        form.value.storeId = store.id;
        form.value.tableId = '';
        storeHours.value = hoursByStore.value[store.id] || [];
        monthClosures.value = closuresByStore.value[store.id] || [];
        clearStepErrors(['storeId']);
        syncBoard(store.id);
        await Promise.all([
          fetchTables(),
          storeHours.value.length ? Promise.resolve() : fetchStoreHours()
        ]);
      } else if (!tables.value.length) {
        await fetchTables();
      } else {
        syncBoard(store.id);
      }
      form.value.reservationTime = slot.time;
      autoAssignTableForSelectedSlot({ force: true });
      clearStepErrors(['reservationTime', 'tableId']);
      await warnIfGuestCountOutOfRange({ announce: false });
      if (!isEditMode.value) currentStep.value = 2;
    };

    const toggleDatePicker = () => {
      datePickerOpen.value = !datePickerOpen.value;
      if (datePickerOpen.value) {
        calendarMonth.value = (form.value.reservationDate || minDate.value).slice(0, 7);
      }
    };

    const shiftCalendarMonth = (delta) => {
      const [yearText, monthText] = calendarMonth.value.split('-');
      const cursor = new Date(Number(yearText), Number(monthText) - 1 + delta, 1);
      const next = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
      if (next < minDate.value.slice(0, 7) || next > maxDate.value.slice(0, 7)) return;
      calendarMonth.value = next;
    };

    const pickDate = (value) => {
      if (!value) return;
      const cell = calendarCells.value.find((item) => item.date === value);
      if (!cell || cell.empty || cell.locked || cell.outside) return;
      form.value.reservationDate = value;
      datePickerOpen.value = false;
      clearStepErrors(['reservationDate', 'reservationTime', 'tableId']);
    };

    const fetchStoreHours = async () => {
      if (!form.value.storeId) {
        storeHours.value = [];
        return;
      }
      try {
        const response = await storeService.getStoreHours(form.value.storeId);
        storeHours.value = response?.success ? (response.data?.hours || []) : [];
        if (form.value.storeId) {
          hoursByStore.value = {
            ...hoursByStore.value,
            [form.value.storeId]: storeHours.value
          };
        }
      } catch (err) {
        console.error('[CreateReservation] Failed to fetch store hours:', err);
        storeHours.value = [];
      }
    };

    const handleStoreChange = () => {
      form.value.tableId = '';
      form.value.reservationTime = '';
      ensureNearestReservationDate();
      fetchTables();
      fetchStoreHours();
    };

    const selectStore = (storeId) => {
      if (!storeId || form.value.storeId === storeId) return;
      form.value.storeId = storeId;
      clearStepErrors(['storeId']);
      handleStoreChange();
    };

    const handleSubmit = async () => {
      if (!isEditMode.value) {
        for (const step of [1, 2, 3]) {
          if (!validateStep(step)) {
            currentStep.value = step;
            await showError('請先完成必填欄位');
            return;
          }
        }
      } else if (![1, 2, 3].every((step) => validateStep(step))) {
        await showError('請先完成必填欄位');
        return;
      }

      if (
        form.value.contactPhone?.replace(/\D/g, '').length >= 6
        && !memberLookupDone.value
      ) {
        if (!await lookupMemberByPhone()) return;
      }

      submitting.value = true;

      try {
        const submitData = {
          ...form.value,
          memberId: form.value.memberId || undefined,
          tableId: form.value.tableId || undefined,
          occasion: form.value.occasion || undefined,
          specialRequests: form.value.specialRequests || undefined
        };

        if (isEditMode.value) {
          await reservationService.updateReservation(route.params.id, submitData);
          showSuccess('訂位更新成功');
          router.push({
            path: '/client/reservations',
            query: { storeId: form.value.storeId || undefined }
          });
        } else {
          const response = await reservationService.createReservation(submitData);
          showSuccess('訂位建立成功');
          const createdId = response?.data?.id;
          if (createdId) {
            router.push(`/client/reservations/${createdId}`);
          } else {
            router.push({
              path: '/client/reservations',
              query: { storeId: form.value.storeId || undefined }
            });
          }
        }
      } catch (err) {
        const errorData = err.response?.data;
        if (errorData?.error?.details) {
          errors.value = errorData.error.details;
        }
        showError(errorData?.message || (isEditMode.value ? '更新訂位失敗' : '建立訂位失敗'));
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/reservations');
    };

    const formatTimeToMinute = (timeString) => {
      if (!timeString) return '';
      const parts = timeString.split(':');
      if (parts.length < 2) return timeString;
      return `${parts[0]}:${parts[1]}`;
    };

    const selectTimeSlot = async (slot) => {
      if (!slot?.available) return;
      form.value.reservationTime = slot.time;
      clearStepErrors(['reservationTime']);
      autoAssignTableForSelectedSlot({ force: true });
      await warnIfGuestCountOutOfRange({ announce: true });
    };

    const formatCurrency = (value) => Number(value || 0).toLocaleString('zh-TW');
    const getStoreImage = (store) => {
      if (!store || !Array.isArray(store.storeImages)) return '';
      return store.storeImages.find((url) => typeof url === 'string' && url.trim()) || '';
    };
    const formatStoreAddress = (store) => {
      if (!store) return '未提供地址';
      const parts = [store.city, store.district, store.street]
        .filter((part) => typeof part === 'string' && part.trim())
        .map((part) => part.trim());
      return parts.length > 0 ? parts.join('') : '未提供地址';
    };

    const getRoomDescription = (table) => {
      if (!table) return '未設定';
      if (table.features && String(table.features).trim()) return table.features;
      return '未設定';
    };

    const fetchExistingReservation = async () => {
      if (!route.params.id) return;

      isEditMode.value = true;
      loading.value = true;

      try {
        const response = await reservationService.getReservation(route.params.id);

        if (response.success && response.data) {
          const reservation = response.data;

          let reservationDate = reservation.reservationDate;
          if (reservationDate && reservationDate.includes('T')) {
            reservationDate = reservationDate.split('T')[0];
          }

          form.value = {
            storeId: reservation.storeId || '',
            reservationDate: reservationDate || '',
            reservationTime: reservation.reservationTime || '',
            guestCount: reservation.guestCount || 1,
            contactName: reservation.contactName || '',
            contactPhone: reservation.contactPhone || '',
            memberId: reservation.memberId || null,
            tableId: reservation.tableId || '',
            occasion: reservation.occasion || '',
            specialRequests: reservation.specialRequests || ''
          };

          if (form.value.storeId) {
            await Promise.all([fetchTables(), fetchStoreHours()]);
            if (form.value.reservationDate) {
              await fetchAvailableSlots();
            }
          }
          if (form.value.contactPhone) {
            await lookupMemberByPhone();
          }
        }
      } catch (err) {
        console.error('[CreateReservation] Failed to fetch existing reservation:', err);
        showError('載入訂位資料失敗');
        router.push('/client/reservations');
      } finally {
        loading.value = false;
      }
    };

    const phoneMedia = typeof window === 'undefined' ? null : window.matchMedia('(max-width: 768px)');
    const syncPreviewLimit = () => {
      previewSlotLimit.value = phoneMedia?.matches ? 4 : 6;
    };

    onMounted(() => {
      syncPreviewLimit();
      phoneMedia?.addEventListener('change', syncPreviewLimit);
      ensureNearestReservationDate(true);
      calendarMonth.value = (form.value.reservationDate || minDate.value).slice(0, 7);
      fetchStores();
      fetchExistingReservation();
    });

    onUnmounted(() => {
      phoneMedia?.removeEventListener('change', syncPreviewLimit);
    });

    watch(minDate, () => {
      ensureNearestReservationDate();
    });

    watch(
      () => form.value.reservationDate,
      (value) => {
        if (!datePickerOpen.value && value) {
          calendarMonth.value = value.slice(0, 7);
        }
      }
    );

    watch(
      () => [stores.value.length, calendarMonth.value],
      () => {
        fetchAllMonthClosures();
      }
    );

    watch(periodTabs, (tabs) => {
      if (slotView.value !== 'all' && !tabs.includes(slotView.value)) {
        slotView.value = 'all';
      }
    });

    watch(
      () => [form.value.reservationDate, form.value.guestCount, stores.value.map((store) => store.id).join(',')],
      () => {
        fetchStoreBoards();
      }
    );

    return {
      form,
      occasionOptions,
      stores,
      selectedStore,
      tables,
      tablesLoaded,
      selectableSlots,
      groupedSlots,
      periodTabs,
      regularSeats,
      privateRooms,
      emptySlotsMessage,
      selectableTables,
      selectedTable,
      selectedTableMatch,
      allowUpwardTableAssignment,
      allDayClosure,
      isWeeklyRestDate,
      availableSlots,
      slotsLoading,
      boardsLoading,
      scheduleTitle,
      loading,
      submitting,
      errors,
      minDate,
      dateLabel,
      datePickerOpen,
      calendarMonthLabel,
      calendarCells,
      canPrevMonth,
      canNextMonth,
      weekdayShort,
      slotView,
      guestOptions,
      isEditMode,
      currentStep,
      steps,
      matchedMember,
      memberLookupLoading,
      memberLookupDone,
      selectStore,
      openStoreSchedule,
      selectPreviewSlot,
      previewTimesFor,
      hasMoreTimes,
      cardEmptyLabel,
      storePhone,
      toggleDatePicker,
      shiftCalendarMonth,
      pickDate,
      selectSeat,
      toggleOccasion,
      formatCapacity,
      isUpwardTable,
      roomCardMeta,
      handleTableChange,
      formatTableOptionLabel,
      formatTimeToMinute,
      selectTimeSlot,
      formatCurrency,
      getStoreImage,
      formatStoreAddress,
      getRoomDescription,
      handlePhoneInput,
      lookupMemberByPhone,
      nextStep,
      previousStep,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped>
.create-reservation-container {
  padding: 20px;
}

.loading-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  min-height: 220px;
}

.spinner {
  animation: spin 0.8s linear infinite;
  border: 3px solid #e5e7eb;
  border-radius: 50%;
  border-top-color: #409eff;
  height: 32px;
  width: 32px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.reservation-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 20px;
}

.reservation-step {
  align-items: center;
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
}

.reservation-step::before {
  background: #dbe3ec;
  content: '';
  height: 2px;
  left: -50%;
  position: absolute;
  right: 50%;
  top: 16px;
}

.reservation-step:first-child::before {
  display: none;
}

.reservation-step > span {
  align-items: center;
  background: #e2e8f0;
  border-radius: 50%;
  color: #64748b;
  display: flex;
  font-weight: 700;
  height: 32px;
  justify-content: center;
  position: relative;
  width: 32px;
  z-index: 1;
}

.reservation-step.active,
.reservation-step.completed {
  color: #1d4ed8;
}

.reservation-step.active > span,
.reservation-step.completed > span {
  background: #2563eb;
  color: #fff;
}

.reservation-step.completed::before {
  background: #2563eb;
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

.form-hint-warning {
  color: #b45309;
  font-weight: 500;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.phone-lookup-field {
  display: grid;
  gap: 5px;
}

.matched-member-card {
  align-items: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding: 14px;
}

.matched-member-card > i {
  color: #2563eb;
  font-size: 28px;
}

.matched-member-card > div {
  display: grid;
  gap: 3px;
}

.matched-member-card span {
  color: #64748b;
  font-size: 13px;
}

.store-picker {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.store-picker-btn {
  border: 1px solid #dbe1ea;
  background: #fff;
  color: #111827;
  border-radius: 10px;
  padding: 0;
  text-align: left;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.store-picker-btn:hover {
  border-color: #409eff;
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.12);
}

.store-picker-btn.is-selected {
  border-color: #409eff;
  background: #f6faff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.store-card-image-wrapper {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f4f6;
}

.store-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.store-card-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 28px;
}

.store-card-content {
  padding: 10px 12px 12px;
}

.store-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.store-card-address {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  min-height: 18px;
}

.store-board {
  display: grid;
  gap: 12px;
  margin: 8px 0 16px;
}

.store-board-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
}

.store-board-card.is-selected {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.store-board-summary {
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.store-board-summary img,
.store-board-photo {
  width: 100%;
  height: 96px;
  object-fit: cover;
  background: #f3f4f6;
}

.store-board-photo {
  display: grid;
  place-items: center;
  color: #9ca3af;
  font-size: 28px;
}

.store-board-copy {
  padding: 12px 12px 0 0;
}

.store-board-copy h4 {
  margin: 0;
  font-size: 16px;
}

.store-board-copy p {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.45;
}

.store-board-phone {
  color: #1d4ed8 !important;
}

.store-board-notice {
  display: grid;
  gap: 2px;
  margin-top: 8px;
}

.store-board-notice strong {
  color: #1d4ed8;
  font-size: 12px;
}

.store-board-notice span {
  color: #374151;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
}

.store-board-empty {
  margin: 0;
  padding: 0 12px 14px;
  color: #6b7280;
  font-size: 13px;
}

.time-row {
  display: flex;
  gap: 8px;
  padding: 12px;
}

.time-row .time-chip {
  flex: 1 1 0;
  min-width: 0;
}

.more-times {
  letter-spacing: 1px;
}

.booking-filters {
  display: grid;
  grid-template-columns: 1.4fr 0.8fr;
  gap: 10px;
  margin-bottom: 12px;
}

.booking-filters.is-invalid .booking-filter {
  border-color: #dc3545;
}

.booking-filter {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  margin: 0;
  padding: 8px 28px 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5 6 6.5 11 1.5' fill='none' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 12px center;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.booking-filter:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.booking-filter span {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.booking-filter strong,
.booking-filter select {
  color: #111827;
  font-size: 15px;
  font-weight: 650;
}

.booking-filter select {
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  appearance: none;
  cursor: pointer;
}

.booking-date-btn {
  font: inherit;
}

.date-picker-panel {
  grid-column: 1 / -1;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.date-picker-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.date-nav-btn {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.date-nav-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.date-weekday-row,
.date-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
}

.date-weekday-row {
  margin-bottom: 4px;
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
}

.date-cell {
  display: grid;
  place-items: center;
  min-height: 42px;
  padding: 4px 0;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.date-cell small {
  font-size: 10px;
  font-weight: 500;
  color: #9ca3af;
}

.date-cell.is-empty {
  visibility: hidden;
}

.date-cell.is-closed,
.date-cell.is-outside {
  color: #d1d5db;
  font-weight: 500;
}

.date-cell.is-selected {
  background: #2563eb;
  color: #fff;
}

.date-cell.is-selected small {
  color: inherit;
}

.date-cell:disabled {
  cursor: default;
}

.date-picker-hint {
  margin: 10px 0 0;
  color: #6b7280;
  font-size: 12px;
  text-align: center;
}

.slot-tabs {
  grid-column: 1 / -1;
  display: flex;
  margin: 0 -4px -4px;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
}

.slot-tabs button {
  position: relative;
  flex: 1 0 auto;
  min-width: 72px;
  padding: 12px 16px 10px;
  border: 0;
  background: transparent;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
}

.slot-tabs button.is-selected {
  color: #1d4ed8;
  font-weight: 650;
}

.slot-tabs button.is-selected::after {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 0;
  height: 2px;
  background: #2563eb;
}

.booking-notice {
  margin: 8px 0 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #eff6ff;
  color: #1e3a8a;
}

.booking-notice h4 {
  margin: 0 0 6px;
  font-size: 13px;
}

.booking-notice p {
  margin: 0;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.5;
}

.period-group {
  margin-top: 16px;
}

.period-group h4 {
  margin: 0 0 8px;
  font-size: 14px;
  color: #4b5563;
}

.time-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 8px;
}

.time-chip {
  min-height: 40px;
  padding: 8px 6px;
  border: 1px solid #dbe3f0;
  border-radius: 12px;
  background: #fff;
  color: #1d4ed8;
  font-weight: 650;
  cursor: pointer;
}

.time-chip:hover:not(:disabled) {
  border-color: #2563eb;
  background: #eff6ff;
}

.time-chip.is-selected {
  border-color: #2563eb;
  background: #2563eb;
  color: #fff;
}

.time-chip:disabled {
  color: #a1a1aa;
  background: #f4f4f5;
  text-decoration: line-through;
  cursor: default;
}

.seat-board {
  margin-top: 18px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fafafa;
}

.seat-board.is-invalid {
  border-color: #dc3545;
}

.seat-board-head h4,
.seat-group h5 {
  margin: 0;
  font-size: 15px;
}

.seat-board-head p {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}

.seat-group {
  margin-top: 14px;
}

.seat-group h5 {
  margin-bottom: 8px;
  color: #374151;
}

.seat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}

.seat-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-height: 72px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  color: #111827;
  text-align: left;
  cursor: pointer;
}

.seat-card strong {
  font-size: 15px;
}

.seat-card span,
.seat-card small {
  color: #6b7280;
  font-size: 12px;
}

.seat-card.is-upward {
  border-color: #fcd34d;
}

.seat-card-room {
  background: #fffdf8;
}

.seat-card.is-selected {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.seat-card.is-selected strong,
.seat-card.is-selected span,
.seat-card.is-selected small {
  color: #1d4ed8;
}

.booking-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  color: #334155;
}

.booking-summary strong {
  color: #0f172a;
}

.occasion-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.occasion-chip {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #111827;
  cursor: pointer;
}

.occasion-chip.is-selected {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 650;
}

.room-summary {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
}

.room-summary-item {
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
}

.room-summary-label {
  font-weight: 600;
}

.wizard-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #e5e7eb;
}

.wizard-actions .btn-primary {
  margin-left: auto;
}

@media (max-width: 768px) {
  .form-row,
  .booking-filters {
    grid-template-columns: 1fr;
  }

  .reservation-step small {
    font-size: 11px;
    text-align: center;
  }
}
</style>
